/* eslint-disable eqeqeq */
/* eslint-disable react-hooks/exhaustive-deps */
import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import useFormWithSchema from "../../hooks/useFormWithSchema";
import * as Yup from "yup";
import { Controller } from "react-hook-form";
import ComboboxInput from "../inputs/ComboboxInput";
import Accordian from "../accordian";
import { useTranslation } from "react-i18next";
import TextInput from "../inputs/TextInput";
import {
  useGetAllBuildingsById,
  useGetAllDepartmentsById,
  useGetAllHosts,
  useGetAllLocationsDropdown,
  useGetAllNationalities,
  useGetAllStatuses,
  useGetAllSystemDetailCodesDropdown,
  useGetDocumentIDTypes,
} from "../../hooks/common/query";
import { formattedDate, mapToOptions } from "../../lib/functions";
import debounce from "lodash.debounce";
import i18n from "../../i18n";
import { useGetAllVisitRequestsReport } from "../../hooks/report/query";
import {
  DOC_TYPE_ID,
  EXPORT_PAGE_SIZE,
  PAGE_NAME,
  SYSTEM_DETAIL_CODES,
} from "../../constants/common";
import { Button } from "../button";
import { ExportVisitRequestsReportForExcelPDFBody } from "../../hooks/report/interface";

interface SearchVisitRequestReportCardProps {
  setAllRequests: Dispatch<SetStateAction<never[]>>;
  page: number;
  setPages: Dispatch<SetStateAction<number>>;
  search: string;
  setCount: Dispatch<SetStateAction<number>>;
  setExportBody: Dispatch<
    SetStateAction<ExportVisitRequestsReportForExcelPDFBody | undefined>
  >;
  pageSize: number;
  setPage: Dispatch<SetStateAction<number>>;
}

const SearchVisitRequestReportCardSchema = Yup.object({
  visitorName: Yup.string(),
  companyName: Yup.string(),
  number: Yup.string().matches(
    /^\d{1,14}$/,
    i18n.language === "en" ? "Enter a valid number." : "أدخل رقماً صحيحاً."
  ),
  nationality: Yup.string(),
  idType: Yup.string(),
  iqama: Yup.string(),
  eid: Yup.string(),
  passportNumber: Yup.string(),
  visaNumber: Yup.string(),
  visitStartDate: Yup.string(),
  visitEndDate: Yup.string(),
  location: Yup.string(),
  building: Yup.string(),
  visitingDepartment: Yup.string(),
  hostEmail: Yup.string(),
  status: Yup.string(),
  visitReqType: Yup.string(),
  visitDurationType: Yup.string(),
});

const initialState = {
  visitorName: "",
  companyName: "",
  number: "",
  location: "",
  building: "",
  status: "",
  visitReqType: "",
  visitStartDate: "",
  visitEndDate: "",
  visitingDepartment: "",
  hostEmail: "",
  nationality: "",
  visitDurationType: "",
};

const SearchVisitRequestReportCard = ({
  setAllRequests,
  page,
  setPages,
  search,
  setCount,
  setExportBody,
  pageSize,
  setPage,
}: SearchVisitRequestReportCardProps) => {
  const {
    control,
    formState: { errors },
    watch,
    reset,
    setValue,
  } = useFormWithSchema(SearchVisitRequestReportCardSchema, {
    defaultValues: {
      visitStartDate: formattedDate(new Date()),
      visitEndDate: formattedDate(new Date()),
      idType: DOC_TYPE_ID.NATIONAL_ID.toString(),
      // location:
      //   user?.n_RoleID !== USER_ROLES_IDS.ADMIN
      //     ? user?.n_LocationID?.toString()
      //       ? user?.n_LocationID?.toString()
      //       : ""
      //     : "",
      location: "",
    },
  });
  const { t } = useTranslation();

  const [selectedLocationId, setSelectedLocationId] = useState<string | number>(
    0
  );
  const [selectedBuildingId, setSelectedBuildingId] = useState<string | number>(
    0
  );
  const [selectedKey, setSelectedKey] = useState<string>("");

  const { data: allStatuses } = useGetAllStatuses();
  const { data: hosts } = useGetAllHosts();
  const { data: allLocations } = useGetAllLocationsDropdown();
  const { data: allDepartments } = useGetAllDepartmentsById({
    encryptedDepartmentID: "0",
    encryptedLocationID: selectedLocationId
      ? selectedLocationId.toString()
      : "",
    n_BuildingID: selectedBuildingId ? selectedBuildingId.toString() : "",
  });
  const { data: allNationalities } = useGetAllNationalities();

  const { data: allVisitDurations } = useGetAllSystemDetailCodesDropdown({
    encryptedId: SYSTEM_DETAIL_CODES.VISIT_DURATION_TYPES,
  });

  const { data: allDocumentIDTypes } = useGetDocumentIDTypes();

  const documentIDTypes = useMemo(
    () =>
      mapToOptions(
        "s_Desc_E",
        "s_Desc_A",
        "n_DetailedCode",
        allDocumentIDTypes
      ),
    [allDocumentIDTypes]
  );

  const departments = useMemo(
    () =>
      mapToOptions(
        "s_DepartmentName_En",
        "s_DepartmentName_Ar",
        "n_DepartmentID",
        allDepartments
      ),
    [allDepartments]
  );

  const hostNames = useMemo(
    () => mapToOptions("fullName", "fullName_Ar", "user_id", hosts),
    [hosts]
  );

  const statuses = useMemo(
    () => mapToOptions("s_Desc_E", "s_Desc_A", "n_DetailedCode", allStatuses),
    [allStatuses]
  );

  const locations = useMemo(
    () =>
      mapToOptions(
        "s_LocationName_En",
        "s_LocationName_Ar",
        "n_LocationID",
        allLocations
      ),
    [allLocations]
  );

  const nationalities = useMemo(
    () => mapToOptions("name", "name_Ar", "iso3", allNationalities),
    [allNationalities]
  );

  const visitDurations = useMemo(
    () =>
      mapToOptions("s_Desc_E", "s_Desc_A", "n_DetailedCode", allVisitDurations),
    [allVisitDurations]
  );

  const visitorName = watch("visitorName");
  const companyName = watch("companyName");
  const number = watch("number");

  const iqama = watch("iqama");
  const visaNumber = watch("visaNumber");
  const passportNumber = watch("passportNumber");
  const eid = watch("eid");

  const [debouncedVisitorName, setDebouncedVisitorName] = useState(visitorName);
  const [debouncedCompanyName, setDebouncedCompanyName] = useState(companyName);
  const [debouncedNumber, setDebouncedNumber] = useState(number);
  const [debouncedIqama, setDebouncedIqama] = useState(iqama);
  const [debouncedVisaNumber, setDebouncedVisaNumber] = useState(visaNumber);
  const [debouncedPassportNumber, setDebouncedPassportNumber] =
    useState(passportNumber);
  const [debouncedEid, setDebouncedEid] = useState(eid);

  const selectedIdType = watch("idType");

  const location = watch("location");
  const building = watch("building");
  const status = watch("status");
  const fromDate = watch("visitStartDate");
  const toDate = watch("visitEndDate");
  const visitReqType = watch("visitReqType");
  const nationality = watch("nationality");
  const visitingDepartment = watch("visitingDepartment");
  const hostEmail = watch("hostEmail");
  const visitDurationType = watch("visitDurationType");

  useEffect(() => {
    setPage(1);
  }, [
    visitorName,
    companyName,
    number,
    nationality,
    eid,
    passportNumber,
    visaNumber,
    iqama,
    fromDate,
    toDate,
    visitReqType,
    visitingDepartment,
    hostEmail,
    visitDurationType,
    building,
    location,
    status,
  ]);

  const debouncedVisitorNameSetter = useMemo(
    () => debounce((value: string) => setDebouncedVisitorName(value), 500),
    []
  );

  const debouncedCompanyNameSetter = useMemo(
    () => debounce((value: string) => setDebouncedCompanyName(value), 500),
    []
  );

  const debouncedNumberSetter = useMemo(
    () => debounce((value: string) => setDebouncedNumber(value), 500),
    []
  );

  const debouncedIqamaSetter = useMemo(
    () => debounce((value: string) => setDebouncedIqama(value), 500),
    []
  );

  useEffect(() => {
    debouncedIqamaSetter(iqama || "");
    return () => debouncedIqamaSetter.cancel();
  }, [iqama, debouncedIqamaSetter]);

  const debouncedEidSetter = useMemo(
    () => debounce((value: string) => setDebouncedEid(value), 500),
    []
  );

  useEffect(() => {
    debouncedEidSetter(eid || "");
    return () => debouncedEidSetter.cancel();
  }, [eid, debouncedEidSetter]);

  const debouncedPassportNumberSetter = useMemo(
    () => debounce((value: string) => setDebouncedPassportNumber(value), 500),
    []
  );

  useEffect(() => {
    debouncedPassportNumberSetter(passportNumber || "");
    return () => debouncedPassportNumberSetter.cancel();
  }, [passportNumber, debouncedPassportNumberSetter]);

  const debouncedVisaNumberSetter = useMemo(
    () => debounce((value: string) => setDebouncedVisaNumber(value), 500),
    []
  );

  useEffect(() => {
    debouncedVisaNumberSetter(visaNumber || "");
    return () => debouncedVisaNumberSetter.cancel();
  }, [visaNumber, debouncedVisaNumberSetter]);

  useEffect(() => {
    debouncedVisitorNameSetter(visitorName || "");
    return () => debouncedVisitorNameSetter.cancel();
  }, [visitorName, debouncedVisitorNameSetter]);

  useEffect(() => {
    debouncedCompanyNameSetter(companyName || "");
    return () => debouncedCompanyNameSetter.cancel();
  }, [companyName, debouncedCompanyNameSetter]);

  useEffect(() => {
    debouncedNumberSetter(number || "");
    return () => debouncedNumberSetter.cancel();
  }, [number, debouncedNumberSetter]);

  const { data: allBuildings } = useGetAllBuildingsById({
    encryptedId: location ? location.toString() : "",
  });

  const buildings = useMemo(
    () =>
      allBuildings
        ? mapToOptions(
            "s_BuildingName_En",
            "s_BuildingName_Ar",
            "n_BuildingID",
            allBuildings
          )
        : [],
    [allBuildings]
  );

  const { data: visitRequests } = useGetAllVisitRequestsReport({
    S_FullName: debouncedVisitorName || "",
    S_Sponsor: debouncedCompanyName || "",
    S_MobileNo: debouncedNumber || "",
    N_LocationId: Number(location) || 0,
    n_BuildingID: building ? Number(building) : 0,
    N_ApprovalStatus: Number(status) || 0,
    FromDate: fromDate,
    ...(toDate && { ToDate: toDate }),
    N_ExternalAppointment: 2,
    N_VisitRequestTypeId: Number(visitReqType) || 0,
    N_DepartmentId: Number(visitingDepartment) || 0,
    Nationality: nationality || "",
    N_PageSize: pageSize,
    N_PageNumber: page,
    S_PageName: PAGE_NAME.APPROVE_APPOINTMENT_BY_STAFF,
    S_Search: search,
    N_HostID: Number(hostEmail) || 0,
    VisitDurationType: Number(visitDurationType) || 0,
    eidNumber:
      selectedIdType == DOC_TYPE_ID.NATIONAL_ID.toString() ? debouncedEid : "",
    passportNumber:
      selectedIdType == DOC_TYPE_ID.PASSPORT.toString()
        ? debouncedPassportNumber
        : "",
    s_Iqama:
      selectedIdType == DOC_TYPE_ID.IQAMA.toString() ? debouncedIqama : "",
    s_VisaNo:
      selectedIdType == DOC_TYPE_ID.VISA.toString() ? debouncedVisaNumber : "",
  });

  useEffect(() => {
    if (visitRequests) {
      setAllRequests(visitRequests.data || []);
      setPages(visitRequests.pagination?.pages || 1);
      setCount(visitRequests.pagination?.count || 0);
    }
  }, [setAllRequests, setPages, setCount, visitRequests]);

  useEffect(() => {
    setExportBody({
      S_FullName: debouncedVisitorName || "",
      S_Sponsor: debouncedCompanyName || "",
      S_MobileNo: debouncedNumber || "",
      N_LocationId: Number(location) || 0,
      n_BuildingID: building ? Number(building) : 0,
      N_ApprovalStatus: Number(status) || 0,
      FromDate: fromDate,
      ...(toDate && { ToDate: toDate }),
      N_ExternalAppointment: 2,
      N_VisitRequestTypeId: Number(visitReqType) || 0,
      N_DepartmentId: Number(visitingDepartment) || 0,
      Nationality: nationality || "",
      N_PageSize: EXPORT_PAGE_SIZE,
      N_PageNumber: page,
      S_PageName: PAGE_NAME.APPROVE_APPOINTMENT_BY_STAFF,
      S_Search: search,
      N_HostID: Number(hostEmail) || 0,
      VisitDurationType: Number(visitDurationType) || 0,
      eidNumber:
        selectedIdType == DOC_TYPE_ID.NATIONAL_ID.toString()
          ? debouncedEid
          : "",
      passportNumber:
        selectedIdType == DOC_TYPE_ID.PASSPORT.toString()
          ? debouncedPassportNumber
          : "",
      s_Iqama:
        selectedIdType == DOC_TYPE_ID.IQAMA.toString() ? debouncedIqama : "",
      s_VisaNo:
        selectedIdType == DOC_TYPE_ID.VISA.toString()
          ? debouncedVisaNumber
          : "",
    });
  }, [
    building,
    debouncedCompanyName,
    debouncedNumber,
    debouncedVisitorName,
    debouncedEid,
    debouncedIqama,
    debouncedPassportNumber,
    debouncedVisaNumber,
    selectedIdType,
    fromDate,
    hostEmail,
    location,
    nationality,
    page,
    search,
    setExportBody,
    status,
    toDate,
    visitReqType,
    visitingDepartment,
    visitDurationType,
  ]);

  const onClear = useCallback(() => {
    reset({ ...initialState, idType: DOC_TYPE_ID.NATIONAL_ID.toString() });
  }, [reset]);

  return (
    <div className="bg-white p-2 md:p-4 rounded-xl flex flex-col gap-3 lg:gap-6 shadow-[0px_1px_5px_0px_rgba(113,112,163,0.3)]">
      <Accordian
        id="1"
        selectedKey={selectedKey}
        onSelectionChange={setSelectedKey}
        title={
          <h1 className="text-xl md:text-2xl flex flex-1 -mt-2">
            {t("searchFilters")}
          </h1>
        }
      >
        <div className="flex flex-col gap-4">
          <form className="flex flex-col gap-4">
            <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 lg:gap-4">
              <Controller
                name="visitorName"
                control={control}
                render={({ field: { name, value, onChange } }) => (
                  <TextInput
                    label={t("visitorName")}
                    onChange={(e) => {
                      onChange(e);
                      debouncedVisitorNameSetter(e.target.value);
                    }}
                    error={errors?.[name]?.message}
                    value={value}
                  />
                )}
              />

              <Controller
                name="companyName"
                control={control}
                render={({ field: { name, value, onChange } }) => (
                  <TextInput
                    label={t("companyName")}
                    onChange={(e) => {
                      onChange(e);
                      debouncedCompanyNameSetter(e.target.value);
                    }}
                    error={errors?.[name]?.message}
                    value={value}
                  />
                )}
              />

              <Controller
                name="number"
                control={control}
                render={({ field: { name, value, onChange } }) => (
                  <TextInput
                    label={t("phoneNumber")}
                    onChange={(e) => {
                      onChange(e);
                      debouncedNumberSetter(e.target.value);
                    }}
                    error={errors?.[name]?.message}
                    value={value}
                    isInfoToolTip
                    infoToolTip={<p>{t("enterPhoneNumber")}</p>}
                  />
                )}
              />

              <Controller
                name="nationality"
                control={control}
                render={({ field: { onChange, name, value } }) => (
                  <ComboboxInput
                    options={nationalities}
                    value={
                      value
                        ? nationalities?.find((item) => item.value === value)
                            ?.labelEn
                        : ""
                    }
                    onChange={onChange}
                    error={errors?.[name]?.message}
                    label={t("nationality")}
                  />
                )}
              />

              <Controller
                name="idType"
                control={control}
                render={({ field: { name, onChange, value } }) => {
                  return (
                    <ComboboxInput
                      options={documentIDTypes}
                      onChange={(value) => {
                        onChange(value === "" ? null : value);
                        setValue("iqama", "");
                        setValue("eid", "");
                        setValue("passportNumber", "");
                        setValue("visaNumber", "");
                      }}
                      error={errors?.[name] && t("idTypeRequired")}
                      value={
                        value
                          ? value == DOC_TYPE_ID.NATIONAL_ID.toString()
                            ? "National ID"
                            : value == DOC_TYPE_ID.PASSPORT.toString()
                            ? "Passport"
                            : value == DOC_TYPE_ID.IQAMA.toString()
                            ? "Iqama"
                            : value == DOC_TYPE_ID.VISA.toString()
                            ? "Visa"
                            : ""
                          : ""
                      }
                      label={t("idType")}
                    />
                  );
                }}
              />
              {selectedIdType == DOC_TYPE_ID.IQAMA.toString() && (
                <Controller
                  name="iqama"
                  control={control}
                  render={({ field: { onChange, name, value } }) => (
                    <TextInput
                      label={t("iqamaNumber")}
                      onChange={onChange}
                      error={errors?.[name]?.message}
                      value={value}
                    />
                  )}
                />
              )}

              {(!selectedIdType ||
                selectedIdType == DOC_TYPE_ID.NATIONAL_ID.toString()) && (
                <Controller
                  name="eid"
                  control={control}
                  render={({ field: { onChange, name, value } }) => (
                    <TextInput
                      label={t("nationalID")}
                      value={value}
                      onChange={onChange}
                      error={errors?.[name]?.message}
                    />
                  )}
                />
              )}

              {selectedIdType == DOC_TYPE_ID.PASSPORT.toString() && (
                <Controller
                  name="passportNumber"
                  control={control}
                  render={({ field: { onChange, name, value } }) => (
                    <TextInput
                      label={t("passportNumber")}
                      onChange={onChange}
                      error={errors?.[name]?.message}
                      value={value}
                    />
                  )}
                />
              )}

              {selectedIdType == DOC_TYPE_ID.VISA.toString() && (
                <Controller
                  name="visaNumber"
                  control={control}
                  render={({ field: { onChange, name, value } }) => (
                    <TextInput
                      label={t("visaNumber")}
                      onChange={onChange}
                      error={errors?.[name]?.message}
                      value={value}
                    />
                  )}
                />
              )}

              <Controller
                name="visitStartDate"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <TextInput
                    type="date"
                    label={t("visitStartDate")}
                    value={value}
                    onChange={(e) => {
                      onChange(e.target.value);
                    }}
                  />
                )}
              />

              <Controller
                name="visitEndDate"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <TextInput
                    type="date"
                    label={t("visitEndDate")}
                    value={value}
                    onChange={(e) => {
                      onChange(e.target.value);
                    }}
                  />
                )}
              />

              <Controller
                name="location"
                control={control}
                render={({ field: { onChange, name, value } }) => (
                  <ComboboxInput
                    options={locations}
                    value={
                      locations?.find((item) => item.value === Number(value))
                        ?.labelEn || ""
                    }
                    onChange={(value) => {
                      onChange(value);
                      setSelectedLocationId(value);
                      setValue("building", "");
                      setValue("visitingDepartment", "");
                    }}
                    error={errors?.[name]?.message}
                    label={t("location")}
                  />
                )}
              />

              <Controller
                name="building"
                control={control}
                render={({ field: { onChange, name, value } }) => (
                  <ComboboxInput
                    options={buildings}
                    value={
                      buildings?.find((item) => item.value === Number(value))
                        ?.labelEn || ""
                    }
                    onChange={(value) => {
                      onChange(value);
                      setSelectedBuildingId(value);
                    }}
                    error={errors?.[name]?.message}
                    label={t("building")}
                  />
                )}
              />

              <Controller
                name="visitingDepartment"
                control={control}
                render={({ field: { onChange, name, value } }) => (
                  <ComboboxInput
                    options={departments}
                    value={
                      departments?.find((item) => item.value === value)
                        ?.labelEn || ""
                    }
                    onChange={onChange}
                    error={errors?.[name]?.message}
                    label={t("department")}
                  />
                )}
              />

              <Controller
                name="hostEmail"
                control={control}
                render={({ field: { onChange, name, value } }) => (
                  <ComboboxInput
                    options={hostNames}
                    value={
                      hostNames?.find((item) => item.value === value)
                        ?.labelEn || ""
                    }
                    onChange={(value) => {
                      onChange(value);
                    }}
                    error={errors?.[name]?.message}
                    label={t("mofaHostName")}
                    isInfoToolTip
                    infoToolTip={<p>{t("enterMofaStaffEmail")}</p>}
                  />
                )}
              />

              <Controller
                name="status"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <ComboboxInput
                    options={statuses}
                    onChange={onChange}
                    label={t("status")}
                    value={
                      statuses?.find((item) => item.value === value)?.labelEn ||
                      ""
                    }
                  />
                )}
              />

              <Controller
                name="visitDurationType"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <ComboboxInput
                    label={t("visitDurationType")}
                    options={visitDurations}
                    value={
                      visitDurations?.find((item) => item.value === value)
                        ?.labelEn || ""
                    }
                    onChange={onChange}
                  />
                )}
              />
            </div>
          </form>

          <div className="flex items-center gap-2">
            <Button
              type="button"
              onClick={onClear}
              className="bg-transparent ring-1 ring-purple-primary text-purple-primary hover:bg-transparent"
            >
              {t("clear")}
            </Button>
          </div>
        </div>
      </Accordian>
    </div>
  );
};

export default React.memo(SearchVisitRequestReportCard);
